import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { CheckCircle, Circle, BookOpen, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Removed local supabase initialization as it's now imported from services/supabaseClient


const DEFAULT_POINTS = [
    { point_id: "APERTURA_PRESENTACION", point_text: "Apertura / Presentación: Identificarse como agente de Imagina Energía.", is_completed: false },
    { point_id: "MOTIVO_LLAMADA_DESCUENTO", point_text: "Motivo de la Llamada / Descuento: Mencionar premio fidelidad y 20% descuento.", is_completed: false },
    { point_id: "CONFIRMACION_DATOS", point_text: "Confirmación de Datos: Confirmar la dirección del suministro.", is_completed: false },
    { point_id: "EXPLICACION_TARIFA_AHORRO", point_text: "Explicación de Tarifa (Ahorro): Mencionar precio 0,119 €/kWh y ahorro inmediato.", is_completed: false },
    { point_id: "ESTABILIDAD_3_ANOS", point_text: "Estabilidad (3 años): Mencionar la garantía de estabilidad durante 3 años.", is_completed: false },
    { point_id: "BENEFICIO_ASESOR_PERSONAL", point_text: "Beneficio Asesor Personal: Explicar la exclusividad del asesor personal.", is_completed: false },
    { point_id: "SVA_CASHBACK_3_MESES_GRATIS", point_text: "SVA_CASHBACK (3 meses gratis): Ofrecer protección tecnológica gratis por 3 meses.", is_completed: false },
    { point_id: "RESUMEN_BENEFICIOS", point_text: "Resumen de Beneficios: Recapitular los 4 puntos clave.", is_completed: false },
    { point_id: "CIERRE_ASUMIDO", point_text: "Cierre Asumido: Confirmar el correo electrónico registrado.", is_completed: false },
    { point_id: "PLAN_AMIGO", point_text: "Plan Amigo: Ofrecer los 40€ por recomendación (Círculo de Soles).", is_completed: false },
    { point_id: "DESPEDIDA", point_text: "Despedida: Desear un excelente día.", is_completed: false }
];

const SalesScriptPanel = ({ contactId }) => {
    const { authFetch } = useAuth();
    const [progress, setProgress] = useState(DEFAULT_POINTS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!contactId) return;

        const fetchProgress = async () => {
            try {
                const res = await authFetch(`/api/sale/script/${contactId}`);
                if (res.ok) {
                    const data = await res.json();
                    const fetchedProgress = data.progress || [];
                    // Merge: Use fetched data if available, otherwise keep default
                    setProgress(prev => prev.map(defaultItem => {
                        const matched = fetchedProgress.find(p => p.point_id === defaultItem.point_id);
                        return matched ? { ...defaultItem, ...matched } : defaultItem;
                    }));
                }
            } catch (err) {
                console.error("Error fetching script progress:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchProgress();

        // Multi-channel subscription: handle both contact_id and tramite_id mappings
        const setupChannel = (filterField) => {
            return supabase
                .channel(`sales-script-${filterField}-${contactId}-${Date.now()}`)
                .on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: 'espuma',
                        table: 'sales_script_progress',
                        filter: `${filterField}=eq.${contactId}`
                    },
                    (payload) => {
                        console.log(`Sales script realtime event (${filterField}):`, payload.eventType, payload);
                        const data = payload.new;
                        if (data && data.point_id) {
                            setProgress(prev => prev.map(item =>
                                item.point_id === data.point_id
                                    ? { ...item, ...data }
                                    : item
                            ));
                        }
                    }
                )
                .subscribe();
        };

        const channelContact = setupChannel('contact_id');
        const channelTramite = setupChannel('tramite_id');

        return () => {
            supabase.removeChannel(channelContact);
            supabase.removeChannel(channelTramite);
        };
    }, [contactId, authFetch]);

    if (!contactId) return null;

    return (
        <div className="fixed top-24 right-8 w-80 bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl overflow-hidden z-30 animate-in fade-in slide-in-from-right-4 duration-500">
            <div className="p-5 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                        <BookOpen size={18} className="text-emerald-500" />
                    </div>
                    <h3 className="font-bold text-slate-100 tracking-tight">Argumentario de Ventas</h3>
                </div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest bg-slate-800 px-2 py-1 rounded">
                    Live Coach
                </div>
            </div>

            <div className="p-4 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar space-y-2">
                {loading && progress.length === 0 ? (
                    <div className="py-10 text-center text-slate-500 text-sm">Cargando guion...</div>
                ) : (
                    progress.map((item, index) => (
                        <div
                            key={item.point_id}
                            className={`group relative flex items-start gap-3 p-3 rounded-xl transition-all duration-500 border ${item.is_completed
                                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-50 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                                : 'bg-slate-800/20 border-transparent text-slate-400 hover:bg-slate-800/40'
                                }`}
                        >
                            <div className="mt-0.5 flex-shrink-0">
                                {item.is_completed ? (
                                    <div className="bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)] rounded-full p-0.5 animate-in zoom-in duration-300">
                                        <CheckCircle size={14} className="text-slate-950" />
                                    </div>
                                ) : (
                                    <Circle size={14} className="text-slate-700 group-hover:text-slate-500 transition-colors" />
                                )
                                }
                            </div>
                            <div className="flex-1 space-y-1">
                                <p className={`text-xs font-medium leading-relaxed ${item.is_completed ? 'text-emerald-300' : 'text-slate-400'}`}>
                                    {item.point_text}
                                </p>
                                {item.is_completed && item.completed_at && (
                                    <p className="text-[9px] text-emerald-500/60 font-mono">
                                        Cumplido a las {new Date(item.completed_at).toLocaleTimeString()}
                                    </p>
                                )}
                            </div>
                            {!item.is_completed && (
                                <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 text-slate-600 transition-all" />
                            )}
                        </div>
                    ))
                )}
            </div>

            <div className="p-4 bg-slate-950/50 border-t border-slate-800/50">
                <div className="flex items-center justify-between text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                    <span>Progreso del Guion</span>
                    <span className="text-emerald-500">
                        {progress.filter(p => p.is_completed).length} / {progress.length}
                    </span>
                </div>
                <div className="mt-2 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-emerald-500 transition-all duration-1000 ease-out"
                        style={{ width: `${(progress.filter(p => p.is_completed).length / (progress.length || 1)) * 100}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

export default SalesScriptPanel;
